import {
  getLeoUuid,
  LeoUUID,
  getList,
  getInt32,
  LeoRPCRequest,
  LeoRPCResponse,
  LeoRPCError,
  LeoRPCResult,
  LeoRPC
} from "@surya-digital/leo-ts-types";
import { SurveyRule, SurveyRuleDTO } from "./surveyRule";
import { SurveyRuleValidationErrorGroup, SurveyRuleValidationErrorGroupDTO, SurveyRuleValidationErrorGroupEnums } from "./surveyRuleValidationErrorGroup";
/* eslint-disable import/export */
export abstract class SaveSurveyRulesRPC implements
  LeoRPC<SaveSurveyRulesRPC.Request, SaveSurveyRulesRPC.Response, SaveSurveyRulesRPC.Errors.Errors>
{
  abstract execute(
    request: SaveSurveyRulesRPC.Request
  ): Promise<LeoRPCResult<SaveSurveyRulesRPC.Response, SaveSurveyRulesRPC.Errors.Errors>>;
}

export namespace SaveSurveyRulesRPC {

  export class InvalidRequestError extends Error {
    constructor(message?: string) {
      super(message);
      Object.setPrototypeOf(this, new.target.prototype);
      this.name = "InvalidRequestError";
    }
  }

  export interface RequestDTO {
    surveyId: string;
    surveyRules: SurveyRuleDTO[];
  }
  
  export class Request implements LeoRPCRequest {
    readonly isSaveSurveyRulesRPCRequest: boolean = true;
  
    readonly surveyId: LeoUUID;
    readonly surveyRules: SurveyRule[];
  
  
    constructor(
    surveyId: LeoUUID, 
    surveyRules: SurveyRule[]
    ) {
      this.surveyId = surveyId;
      this.surveyRules = surveyRules;
    }
  
    static fromDTO(dto: RequestDTO): Request {
      const surveyId = getLeoUuid(dto, "surveyId", InvalidRequestSurveyIdError);
      const surveyRules = getList<SurveyRuleDTO, SurveyRule>(
        dto,
        "surveyRules",
        SurveyRule.fromDTO,
        InvalidRequestSurveyRulesError
      );
      return new Request(
        surveyId,
        surveyRules
      );
    }
  
    toDTO(): RequestDTO {
      const dto: RequestDTO = {
          surveyId: this.surveyId.toDTO(),
          surveyRules: this.surveyRules.map((e: SurveyRule) => { return e.toDTO() })
      };
      return dto;
    }
    }
  
  Request.prototype.toString = function toString(): string {
    return `SaveSurveyRulesRPC(surveyId='${this.surveyId}',surveyRules='${this.surveyRules}')`;
  };
  

  export class InvalidResponseError extends Error {
    constructor(message?: string) {
      super(message);
      Object.setPrototypeOf(this, new.target.prototype);
      this.name = "InvalidResponseError";
    }
  }

  export interface ResponseDTO {}

  export class Response implements LeoRPCResponse {
    static fromDTO(): Response {
      return new Response();
    }

    toDTO(): ResponseDTO {
      return {}
    }
  }

  Response.prototype.toString = function toString(): string {
    return `SaveSurveyRulesRPC.Response()`;
  };

  export enum RPCError {
    InvalidSurveyId = "INVALID_SURVEY_ID",
    ProjectAlreadyArchived = "PROJECT_ALREADY_ARCHIVED",
    MaxLimitExceeded = "MAX_LIMIT_EXCEEDED",
    InvalidSurveyRules = "INVALID_SURVEY_RULES"
  }

  export namespace Errors {
    
    export interface InvalidSurveyIdDTO {
      code: string;
    }
    
    export class InvalidSurveyId extends LeoRPCError<RPCError> {
      readonly isInvalidSurveyId: boolean = true;
    
    
      constructor(
      ) {
        const code = RPCError.InvalidSurveyId;
        super(code);
      }
    
    
      toDTO(): InvalidSurveyIdDTO {
        const dto: InvalidSurveyIdDTO = {
          code: this.code,
        };
        return dto;
      }
    
      }
    
    InvalidSurveyId.prototype.toString = function toString(): string {
      return `SaveSurveyRulesRPC.INVALID_SURVEY_ID()`;
    };
    
    
    export interface ProjectAlreadyArchivedDTO {
      code: string;
    }
    
    export class ProjectAlreadyArchived extends LeoRPCError<RPCError> {
      readonly isProjectAlreadyArchived: boolean = true;
    
    
      constructor(
      ) {
        const code = RPCError.ProjectAlreadyArchived;
        super(code);
      }
    
    
      toDTO(): ProjectAlreadyArchivedDTO {
        const dto: ProjectAlreadyArchivedDTO = {
          code: this.code,
        };
        return dto;
      }
    
      }
    
    ProjectAlreadyArchived.prototype.toString = function toString(): string {
      return `SaveSurveyRulesRPC.PROJECT_ALREADY_ARCHIVED()`;
    };
    
    
    export interface MaxLimitExceededDTO {
      code: string;
      maxLimit: number;
    }
    
    export class MaxLimitExceeded extends LeoRPCError<RPCError> {
      readonly isMaxLimitExceeded: boolean = true;
    
      readonly maxLimit: number;
    
      constructor(
      maxLimit: number
      ) {
        const code = RPCError.MaxLimitExceeded;
        super(code);
        this.maxLimit = maxLimit;
      }
    
      static fromDTO(dto: MaxLimitExceededDTO): MaxLimitExceeded {
        const maxLimit = getInt32(dto, "maxLimit", InvalidResponseMaxLimitExceededMaxLimitError);
        return new MaxLimitExceeded(
          maxLimit
        );
      }
    
      toDTO(): MaxLimitExceededDTO {
        const dto: MaxLimitExceededDTO = {
          code: this.code,
          maxLimit: this.maxLimit
        };
        return dto;
      }
    
      }
    
    MaxLimitExceeded.prototype.toString = function toString(): string {
      return `SaveSurveyRulesRPC.MAX_LIMIT_EXCEEDED(maxLimit='${this.maxLimit}')`;
    };
    
    
    export interface InvalidSurveyRulesDTO {
      code: string;
      validationErrorGroup: SurveyRuleValidationErrorGroupDTO[];
    }
    
    export class InvalidSurveyRules extends LeoRPCError<RPCError> {
      readonly isInvalidSurveyRules: boolean = true;
    
      readonly validationErrorGroup: SurveyRuleValidationErrorGroup[];
    
      constructor(
      validationErrorGroup: SurveyRuleValidationErrorGroup[]
      ) {
        const code = RPCError.InvalidSurveyRules;
        super(code);
        this.validationErrorGroup = validationErrorGroup;
      }
    
      static fromDTO(dto: InvalidSurveyRulesDTO): InvalidSurveyRules {
        const validationErrorGroup = getList<SurveyRuleValidationErrorGroupDTO, SurveyRuleValidationErrorGroup>(
          dto,
          "validationErrorGroup",
          SurveyRuleValidationErrorGroup.fromDTO,
          InvalidResponseInvalidSurveyRulesValidationErrorGroupError
        );
        return new InvalidSurveyRules(
          validationErrorGroup
        );
      }
    
      toDTO(): InvalidSurveyRulesDTO {
        const dto: InvalidSurveyRulesDTO = {
          code: this.code,
          validationErrorGroup: this.validationErrorGroup.map((e: SurveyRuleValidationErrorGroup) => { return e.toDTO() })
        };
        return dto;
      }
    
      }
    
    InvalidSurveyRules.prototype.toString = function toString(): string {
      return `SaveSurveyRulesRPC.INVALID_SURVEY_RULES(validationErrorGroup='${this.validationErrorGroup}')`;
    };
    

    export type Errors = InvalidSurveyId | ProjectAlreadyArchived | MaxLimitExceeded | InvalidSurveyRules;
    export type ErrorsDTO = InvalidSurveyIdDTO | ProjectAlreadyArchivedDTO | MaxLimitExceededDTO | InvalidSurveyRulesDTO;

    export const fromDTO = (dto: ErrorsDTO): Errors => {
      switch (dto.code) {
        case RPCError.InvalidSurveyId:
          return new InvalidSurveyId();
        case RPCError.ProjectAlreadyArchived:
          return new ProjectAlreadyArchived();
        case RPCError.MaxLimitExceeded:
          return MaxLimitExceeded.fromDTO(dto as MaxLimitExceededDTO);
        case RPCError.InvalidSurveyRules:
          return InvalidSurveyRules.fromDTO(dto as InvalidSurveyRulesDTO);
        default:
          throw new InvalidResponseError(`Unexpected error code: ${dto.code}`);
      }
    };
  }
}

export class InvalidRequestSurveyIdError extends SaveSurveyRulesRPC.InvalidRequestError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidRequestSurveyIdError";
  }
}

export class InvalidRequestSurveyRulesError extends SaveSurveyRulesRPC.InvalidRequestError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidRequestSurveyRulesError";
  }
}


export class InvalidResponseInvalidSurveyIdError extends SaveSurveyRulesRPC.InvalidResponseError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidResponseInvalidSurveyIdError";
  }
}

export class InvalidResponseProjectAlreadyArchivedError extends SaveSurveyRulesRPC.InvalidResponseError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidResponseProjectAlreadyArchivedError";
  }
}

export class InvalidResponseMaxLimitExceededError extends SaveSurveyRulesRPC.InvalidResponseError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidResponseMaxLimitExceededError";
  }
}

export class InvalidResponseMaxLimitExceededMaxLimitError extends InvalidResponseMaxLimitExceededError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidResponseMaxLimitExceededMaxLimitError";
  }
}
export class InvalidResponseInvalidSurveyRulesError extends SaveSurveyRulesRPC.InvalidResponseError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidResponseInvalidSurveyRulesError";
  }
}

export class InvalidResponseInvalidSurveyRulesValidationErrorGroupError extends InvalidResponseInvalidSurveyRulesError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidResponseInvalidSurveyRulesValidationErrorGroupError";
  }
}
